<template>
  <div class="browse-page">
    <div class="sidebar-wrapper">
      <button class="hamburger" @click="sidebarOpen = !sidebarOpen">☰</button>
      <div class="sidebar" :class="{ open: sidebarOpen }">
        <div class="sidebar-header">
          <button class="close-btn" @click="sidebarOpen = false">✖</button>
          <p>{{ user.fullname }}</p>
          <p>{{ user.phone_number }}</p>
        </div>
        <ul class="sidebar-menu">

          <template v-if="user && user.user_type === 'Pet Owner'">
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li class="has-submenu" @click="petsOpen = !petsOpen">
              My Pets
              <span class="arrow" :class="{ 'arrow-open': petsOpen }">›</span>
            </li>
            <ul class="sub-menu" :class="{ 'sub-open': petsOpen }">
              <li @click="goToPage('YourPetsPage')">View My Pets</li>
              <li @click="goToPage('AddPetPage')">Add Pet</li>
              <li @click="goToPage('MeetingsPage')">Booking Requests</li>
            </ul>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

          <template v-else>
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li @click="goToPage('UsersFavouritesPage')">My Favourites</li>
            <li @click="goToPage('CalendarPage')">My Calendar</li>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

        </ul>
      </div>
    </div>

    <div class="page-wrapper">

      <div class="page-header">
        <h1 class="page-title">Browse Pets</h1>
        <p class="page-sub">Find a furry friend near you</p>
      </div>

      <!-- FILTER BAR -->
      <div class="filters">
        <div class="filter-chip">
          <span class="chip-label">Breed</span>
          <select v-model="filters.breed" class="chip-select">
            <option value="">All</option>
            <option v-for="breed in breedOptions" :key="breed.id" :value="breed.name">{{ breed.name }}</option>
          </select>
        </div>

        <div class="filter-sep"></div>

        <div class="filter-chip no-arrow">
          <span class="chip-label">Age</span>
          <div class="pill-row">
            <button
              v-for="age in ['Puppy','Adult','Senior']"
              :key="age"
              class="pill"
              :class="{ 'pill-active': filters.age === age }"
              @click="filters.age = filters.age === age ? '' : age"
            >{{ age }}</button>
          </div>
        </div>

        <div class="filter-sep"></div>

        <div class="filter-chip no-arrow">
          <span class="chip-label">Gender</span>
          <div class="pill-row">
            <button class="pill pill-blue" :class="{ 'pill-active': filters.gender === 'Male' }" @click="filters.gender = filters.gender === 'Male' ? '' : 'Male'">Male</button>
            <button class="pill pill-pink" :class="{ 'pill-active': filters.gender === 'Female' }" @click="filters.gender = filters.gender === 'Female' ? '' : 'Female'">Female</button>
          </div>
        </div>

        <div class="filter-sep"></div>

        <div class="filter-chip no-arrow distance-chip">
          <div class="distance-header">
            <span class="chip-label">Distance</span>
            <span class="distance-badge">{{ filters.distance }} km</span>
          </div>
          <input class="distance-slider" type="range" min="1" max="30" v-model="filters.distance" />
        </div>
      </div>

      <!-- PET GRID -->
      <div class="pet-grid">
        <div v-for="pet in filteredPets" :key="pet.id" class="pet-card">
          <div class="pet-card-img-wrap">
            <img
              :src="backendUrl + '/pet-pics/' + pet.pet_photo"
              :alt="pet.pet_name"
              class="pet-card-img"
            />
            <div class="online-dot"></div>
            <button @click.stop="toggleFavourite(pet.id)" class="fav-btn" :class="{ 'fav-active': isFavourite(pet.id) }">
              <span>{{ isFavourite(pet.id) ? '❤️' : '🤍' }}</span>
            </button>
          </div>
          <div class="pet-body">
            <div class="pet-top">
              <p class="pet-name">{{ pet.pet_name }}</p>
            </div>
            <p class="pet-breed">{{ pet.breed }}</p>
            <div class="badges">
              <span class="badge badge-age">{{ pet.age }}</span>
              <span class="badge" :class="pet.gender === 'Male' ? 'badge-male' : 'badge-female'">{{ pet.gender }}</span>
            </div>
            <button class="view-btn" @click="$emit('view-pet-profile', pet)">View Profile</button>
          </div>
        </div>

        <div v-if="filteredPets.length === 0" class="empty-state">
          <p class="empty-icon">🐾</p>
          <p class="empty-text">No pets found nearby</p>
          <p class="empty-sub">Try adjusting your filters</p>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';

const backendUrl = process.env.VUE_APP_API_URL;

export default {
  props: {
    user: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      backendUrl,
      pets: [],
      breedOptions: [],    // ← NEW
      filters: {
        breed: '',
        age: '',
        gender: '',
        distance: 30
      },
      userCoords: null,
      sidebarOpen: false,
      favourites: [],
      petsOpen: false,
    };
  },
  computed: {
    filteredPets() {
      return this.pets.filter(pet => {
        if (!pet.isOnline) return false;
        const matchBreed = !this.filters.breed || pet.breed === this.filters.breed;
        const matchGender = !this.filters.gender || pet.gender === this.filters.gender;
        const matchAge   = !this.filters.age   || pet.age    === this.filters.age;

        // distance logic unchanged…
        if (!this.userCoords) return matchBreed && matchGender && matchAge;

        let lat, lon;
        if (typeof pet.location === 'string') {
          const [a,b] = pet.location.split(',').map(s=>s.trim());
          lat = parseFloat(a); lon = parseFloat(b);
        } else if (pet.location?.coordinates) {
          [lon, lat] = pet.location.coordinates;
        } else if (pet.location?.lat && pet.location?.lon) {
          lat = pet.location.lat; lon = pet.location.lon;
        }

        let matchDistance = true;
        if (!isNaN(lat) && !isNaN(lon)) {
          const d = this.calculateDistance(lat, lon);
          matchDistance = d <= this.filters.distance;
        }
        return matchBreed && matchGender && matchAge && matchDistance;
      });
    }
  },
  methods: {
    goToPage(pageName) {
      this.sidebarOpen = false;
      this.$emit('change-page', pageName);
    },
    goToBooking(petId) {
      this.$emit('change-page', 'BookPetAppointmentPage', { petId });
    },
    async fetchPets() {
      try {
        const { data } = await axios.get(`${backendUrl}/api/pets/pets`, { withCredentials: true }, {
          
        })
        this.pets = data
      } catch (err) {
        console.error('Error fetching pets:', err);
      }
    },
    async fetchBreedOptions() {
      try {
        const { data } = await axios.get(`${backendUrl}/api/breeds/breeds`, { withCredentials: true });
        this.breedOptions = data;
      } catch (err) {
        console.error('Error fetching breeds:', err);
      }
    },
    calculateDistance(lat, lon) {
      if (!this.userCoords) return Infinity;
      const R = 6371;
      const dLat = this.deg2rad(lat  - this.userCoords.lat);
      const dLon = this.deg2rad(lon  - this.userCoords.lon);
      const a = Math.sin(dLat/2)**2
              + Math.cos(this.deg2rad(this.userCoords.lat))
              * Math.cos(this.deg2rad(lat))
              * Math.sin(dLon/2)**2;
      return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    },
    deg2rad(d) { return d * Math.PI/180; },
    detectLocation() {
      navigator.geolocation.getCurrentPosition(
        pos => this.userCoords = { lat: pos.coords.latitude, lon: pos.coords.longitude },
        err => console.error('Location error:', err)
      );
    },
    applyFilters() {
      // just re-assign to trigger reactivity
      this.filters = { ...this.filters };
    },
    async toggleFavourite(petId) {
      try {
        const response = await axios.patch(
          `${backendUrl}/pets/users/${this.user.id}/favourites`,
          { petId },
          { withCredentials: true }
        );
        this.favourites = response.data.favourites;

        localStorage.setItem('favourites', JSON.stringify(this.favourites));
      } catch (error) {
        console.error('Error toggling favourite:', error);
      }
    },
    isFavourite(petId) {
      return Array.isArray(this.favourites) && this.favourites.includes(petId);
    },
    async fetchFavourites() {
      try {
        const { data } = await axios.get(
          `${backendUrl}/api/pets/users/${this.user.id}/favourites`,
          { withCredentials: true }
        );
        // previously you did:
        // this.favourites = data.favourites
        // but your GET now returns an array of pet objects, not { favourites: [...] }
        // so you need to map out their IDs:
        this.favourites = Array.isArray(data)
          ? data.map(pet => pet.id)
          : (data.favourites || []);
      } catch (err) {
        console.error('Error fetching favourites:', err);
      }
    },
  },
  mounted() {
    this.fetchBreedOptions();  // ← NEW
    this.fetchPets();
    this.detectLocation();
    this.fetchFavourites();
  },
  activated() {
  this.fetchFavourites(); // re-fetch favourites when component becomes active
  }
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.browse-page {
  min-height: 100vh;
  background: #fffbe8;
}

/* ── Page wrapper ── */
.page-wrapper {
  width: 100%;
  max-width: 100vw;
  padding: 60px 10px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ── Page header ── */
.page-header {
  width: 100%;
  max-width: 1200px;
  padding: 0 0 0 4px;
  margin-bottom: 20px;
}

.page-title {
  font-size: 26px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.4px;
  margin: 0;
}

.page-sub {
  font-size: 14px;
  color: #aaa;
  font-weight: 500;
  margin-top: 4px;
}

/* ── Filter Bar ── */
.filters {
  position: sticky;
  top: 5px;
  z-index: 100;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0;
  padding: 0;
  margin: 0 auto 24px;
  max-width: 1150px;
  width: 100%;
  background: #fffbe8;
  border: 1.5px solid #e8e8e8;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.filter-chip {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 18px;
  flex: 1;
  min-width: 0;
  transition: background 0.15s;
  width: 100px;
  align-items: center;
  text-align: center;
}

.filter-chip:hover { background: #ffe876; }

.chip-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #999;
}

.chip-select {
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  border: none;
  outline: none;
  font-size: 14px;
  font-weight: 600;
  color: #222;
  cursor: pointer;
  width: 100%;
  padding: 0;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23aaa' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0px center;
  padding-right: 16px;
}

.filter-sep {
  width: 1.5px;
  align-self: stretch;
  background: #ffefa1;
  margin: 8px 0;
  flex-shrink: 0;
}

.pill-row {
  display: flex;
  gap: 6px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}

.pill {
  padding: 4px 12px;
  border-radius: 999px;
  border: 1.5px solid #ffd710;
  background: #fff2b3;
  color: #555;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
  white-space: nowrap;
}

.pill:hover { border-color: #bbb; background: #eee; color: #222; }

.pill.pill-active {
  background: #7c3aed;
  border-color: #7c3aed;
  color: #fff;
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.3);
}

.pill.pill-blue.pill-active {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.3);
}

.pill.pill-pink.pill-active {
  background: #db2777;
  border-color: #db2777;
  color: #fff;
  box-shadow: 0 2px 8px rgba(219, 39, 119, 0.3);
}

.distance-chip { min-width: 160px; }

.distance-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
}

.distance-badge {
  font-size: 12px;
  font-weight: 700;
  background: #fff3b0;
  color: #92610a;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid #f6d860;
}

.distance-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 4px;
  border-radius: 4px;
  background: linear-gradient(to right, #f59e0b 0%, #e5e7eb 100%);
  outline: none;
  cursor: pointer;
  margin-top: 4px;
}

.distance-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #f59e0b;
  border: 3px solid #fff;
  box-shadow: 0 1px 6px rgba(0,0,0,0.2);
  cursor: pointer;
  transition: transform 0.15s;
}

.distance-slider::-webkit-slider-thumb:hover { transform: scale(1.2); }

.distance-slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #f59e0b;
  border: 3px solid #fff;
  box-shadow: 0 1px 6px rgba(0,0,0,0.2);
  cursor: pointer;
}

/* ── Pet Grid ── */
.pet-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  padding: 4px 0 28px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
}

/* ── Pet Card ── */
.pet-card {
  background: #ffffff;
  border-radius: 24px;
  border: 1px solid #f0f0f0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s ease;
  cursor: default;
}

.pet-card:hover {
  transform: translateY(-6px) scale(1.01);
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.11);
}

.pet-card-img-wrap {
  position: relative;
  width: 100%;
  height: 175px;
  background: #f7f3ee;
  overflow: hidden;
}

.pet-card-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.4s ease;
}

.pet-card:hover .pet-card-img { transform: scale(1.06); }

.online-dot {
  position: absolute;
  top: 12px;
  left: 13px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #22c55e;
  border: 2.5px solid #ffffff;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.2);
}

.fav-btn {
  position: absolute;
  top: 10px;
  right: 11px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(6px);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), background 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.10);
  line-height: 1;
}

.fav-btn:hover { transform: scale(1.2); background: rgba(255,255,255,1); }
.fav-btn.fav-active { background: #fff0f3; }

.pet-body {
  padding: 15px 16px 17px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
}

.pet-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.pet-name {
  font-size: 16px;
  font-weight: 800;
  color: #111;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.2px;
}

.pet-breed {
  font-size: 12px;
  color: #aaa;
  margin: 0;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.badges {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.badge {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  display: inline-block;
  letter-spacing: 0.01em;
}

.badge-age    { background: #f3eeff; color: #6d28d9; }
.badge-male   { background: #eff6ff; color: #1d4ed8; }
.badge-female { background: #fdf2f8; color: #be185d; }

.view-btn {
  margin-top: auto;
  padding: 10px 14px;
  background: #eac7ff;
  color: black;
  border: none;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  width: 100%;
  transition: background 0.18s, transform 0.1s;
  letter-spacing: 0.02em;
  margin-top: 10px;
}

.view-btn:hover  { background: #ba71ff; }
.view-btn:active { transform: scale(0.97); }

/* ── Empty state ── */
.empty-state {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 8px;
}

.empty-icon { font-size: 40px; }
.empty-text { font-size: 16px; font-weight: 700; color: #333; margin: 0; }
.empty-sub  { font-size: 13px; color: #bbb; margin: 0; }

/* ── Hamburger ── */
.hamburger {
  position: fixed;
  top: 20px;
  left: 20px;
  font-size: 28px;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 1000;
  color: #4a1070;
}

/* ── Welcome Banner ────────────────────────── */
.greeting {
  font-size: 22px;
  font-weight: 800;
  color: #1f1f1f;
}

.main-content {
  font-size: 15px;
  color: #333;
  opacity: 0.85;
}


.text-block {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.greeting {
  font-size: 22px;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: -0.3px;
  margin: 0;
  line-height: 1.2;
  text-shadow: 0 1px 4px rgba(0,0,0,0.12);
}

.main-content {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.78);
  margin: 0;
  font-weight: 500;
}

.book-button {
  margin-top: 6px;
  align-self: flex-start;
  border-radius: 999px;
  padding: 10px 22px;
  background: #fffbe8;
  border: none;
  color: #7a4f00;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(255, 214, 0, 0.45);
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s;
  letter-spacing: 0.01em;
}

.book-button:hover {
  transform: translateY(-2px) scale(1.04);
  box-shadow: 0 8px 22px rgba(255, 214, 0, 0.55);
}

.book-button:active {
  transform: scale(0.97);
}

.banner-pic {
  position: relative;
  z-index: 1;
  height: 130px;
  width: auto;
  object-fit: contain;
  filter: drop-shadow(0 6px 16px rgba(0,0,0,0.18));
  flex-shrink: 0;
  margin-left: 1355px;
  margin-bottom: 20px;
}
/* ── Responsive ── */
@media (max-width: 700px) {
  .filters {
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    gap: 10px;
    padding: 10px;
    scroll-snap-type: x mandatory;
  }
  .filter-chip {
    flex: 0 0 auto;
    min-width: 140px;
    width: auto;
    scroll-snap-align: start;
  }
  .filter-sep { display: none; }
  .pet-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  .pet-card-img-wrap { height: 135px; }
  .pet-name  { font-size: 14px; }
  .pet-breed { font-size: 12px; }
}

@media (max-width: 400px) {
  .pet-grid { grid-template-columns: 1fr; }
}

@media (max-width: 600px) {
  .hamburger { font-size: 24px; }
}
</style>